import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
// Types
import { OrganisationProps } from 'types/user';
// Utils
import { useSetState, validArray } from 'utils';
import * as userUtils from 'utils/user';
// App Context
import { useAppDispatch } from 'context/AppContext';
// Admin Context
import { useAdminDispatch, useAdminState } from 'context/AdminContext';
import { organisationsList } from 'context/AdminContext/constants';
import { updateUser, deleteUser } from 'context/AdminContext/middleware';
// Elements
import Alert from 'elements/Alert';
import DateDropdown from 'elements/DateDropdown';
import Button from 'elements/Button';
// Components
import { useAuthState } from 'context/AuthContext';
import SiteCard from './SiteCard';
import SiteModal from './SiteModal';
// Style
import styles from './style/user.module.css';

interface OrganisationStateProps {
  isdefaultOrganisation: boolean,
  isNewOrganisation: boolean,
  organisation: { orgDescription: string },
  roles: [],
}

function User({ newUser = false }) {
  // History
  const history = useHistory();
  // App State
  const appDispatch = useAppDispatch();
  // Admin State
  const adminDispatch = useAdminDispatch();
  const { user } = useAdminState();
  const { defaultOrganisation } = useAuthState();
  // State
  const initialState = userUtils.setUserState(user); 
  const [state, setState] = useSetState(initialState);
  const [error, setError] = useState('');
  const availableOrganisations = userUtils.getAvailableOrganisations(organisationsList, state.organisations);
  // ModalState
  const initialModalState = {
    active: false,
    isdefaultOrganisation: false,
    organisation: {},
  }
  const [modalState, setModalState] = useSetState(initialModalState);

  function renderOrganisationCards(organisation: OrganisationProps) {
    const { roles, orgDescription, orgRef } = organisation;
    return (
      <SiteCard
        handleEdit={() => setModalState({
          active: true,
          isdefaultOrganisation: defaultOrganisation === orgDescription,
          organisation,
        })}
        handleDelete={() => setState({
          defaultOrganisation: defaultOrganisation === orgDescription ? '' : defaultOrganisation,
          organisations: userUtils.deleteOrganisation(orgRef, state.organisations),
        })}
        isHomeOrganisation={defaultOrganisation === orgDescription}
        key={orgDescription}
        roles={validArray(roles) ? roles : []}
        orgDescription={orgDescription}
      />
    );
  }

  function handleConfirm(organisationState:OrganisationStateProps) {
    const {
      isdefaultOrganisation,
      isNewOrganisation,
      organisation,
      roles,
    } = organisationState;
    
    function removeTargetOrganisation(targetOrganisation:OrganisationProps) {
      return targetOrganisation.orgDescription !== organisation.orgDescription;
    }

    if (isNewOrganisation) {
      setState({
        organisations: [...state.organisations, {
          ...organisation,
          roles,
        }],
      })
    } else {
      setState({
        organisations: [...state.organisations.filter(removeTargetOrganisation), {
          ...organisation,
          roles,
        }],
      })
    }
    if (isdefaultOrganisation) {
      setState({
        defaultOrganisation: organisation.orgDescription,
      })
    } else {
      setState({
        defaultOrganisation: defaultOrganisation === organisation.orgDescription ? '' : state.defaultOrganisation,
      })
    }
    setModalState(initialModalState);
  }

  async function handleSubmit() {
    if (!state.startDate) {
      setError('Please Select a Start Date');
      return;
    }
    /* if (!state.defaultOrganisation) {
      setError('Please Select a Default Organisation');
      return;
    } */
    await updateUser({
      appDispatch,
      adminDispatch,
      userId: state.userId,
      newUser,
      data: {
        firstName: state.firstName,
        lastName: state.lastName,
        startDate: state.startDate,
        endDate: state.endDate,
        // defaultOrganisation: state.defaultOrganisation,
        organisations: state.organisations,
      },
    });
    if (state.organisations.length === 0) {
      await deleteUser({
        adminDispatch,
        newUser,
        userId: state.userId,
      });
    }
  }

  useEffect(() => {
    setError('');
  }, [state])

  return (
    <section className={styles.container}>
      <div className={styles.updateBar}>
        <div className={styles.updateBar__dates}>
          <DateDropdown
            dateFormat="DD MMM YYYY"
            minDate={moment()}
            maxDate={state.endDate ? moment(state.endDate).subtract(1, 'day') : null}
            onChange={(date:string) => setState({ startDate: moment(date).format('DD-MMM-YYYY') })}
            placeholder="Start Date"
            selected={state.startDate ? moment(state.startDate) : null}
            value=""
          />
          <DateDropdown
            dateFormat="DD MMM YYYY"
            minDate={state.startDate && moment(state.startDate).add(1, 'day')}
            onChange={(date:string) => setState({ endDate: moment(date).format('DD-MMM-YYYY') })}
            placeholder="End Date"
            selected={state.endDate ? moment(state.endDate) : null}
            value=""
          />
        </div>
        <div className={styles.updateBar__actions}>
          <Button btnStyle="light" handler={() => history.goBack()}>
            Cancel
          </Button>
          <Button
            btnStyle="secondary"
            handler={() => handleSubmit()}
          >
            {newUser ? 'Save' : 'Update User'}
          </Button>
        </div>
      </div>
      <section className={`wrapper ${styles.main}`}>
        <div className="wrapper__content">
          <Alert
            active={Boolean(error)}
            label={error}
            type="error"
          />
        </div>
        <div className={`wrapper__content wrapper__content_grid ${styles.main__grid}`}>
          {validArray(state.organisations) && state.organisations.sort(userUtils.sortByorgRef).map(renderOrganisationCards)}
          { validArray(availableOrganisations) && (
            <div className={styles.addOrganisation}>
              <Button
                btnStyle="tertiary"
                handler={() => setModalState({
                  active: true,
                  isdefaultOrganisation: false,
                  organisation: {},
                })}
              >
                Add Organisation
              </Button>
            </div>
          )}
        </div>
      </section>
      <SiteModal
        active={modalState.active}
        isdefaultOrganisation={modalState.isdefaultOrganisation}
        handleCancel={() => setModalState(initialModalState)}
        handleConfirm={(organisationState:OrganisationStateProps) => handleConfirm(organisationState)}
        organisation={modalState.organisation}
        organisations={availableOrganisations}
      />
    </section>
  );
}

export default User;